import { useState, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import { useForm, FormProvider } from "react-hook-form";
import { useStateMachine } from "little-state-machine";
import { FormattedMessage } from "react-intl";
import { useQueryClient } from "@tanstack/react-query";

// import components
import { useParams } from "react-router-dom";
import usePermissions from "hooks/usePermissions";
import { useGetShipmentBasic } from "api/queries/shipments/useGetShipmentBasic";
import { useGetQuoteRequestDetails } from "api/queries/quotes/useGetQuoteRequestDetails";
import { useGetCustomLongfield01 } from "api/queries/quotation/useGetCustomLongfield01";
import { useGetProviderOptions } from "api/queries/quotes/useGetProviderOptions";
import { useGetQuickRequestDetails } from "api/queries/quick-request/useGetQuickRequestDetails";
import CancelRequestDialog from "components/molecules/TopNav/CancelRequestDialog";
import { quoteRequestQueryKeys } from "api/queries/quotes/quoteRequestQueryKeys";
import { shipmentQueryKeys } from "api/queries/shipments/shipmentQueryKeys";
import { updateBackgroundLoadingState } from "store/store";
import Alert from "components/v2/Alert/Alert";
import QuoteMultiDropBanner from "pages/common/QuotesDetail/components/QuoteMultiDropBanner/QuoteMultiDropBanner";
import Comments from "pages/shipper/ShipmentQuotesV2/components/Comments/Comments";
import InvitedProviders from "pages/shipper/ShipmentQuotesV2/components/InvitedProviders/InvitedProviders";
import CancelRequestBanner from "./components/CancelRequestBanner";

import ApprovalLog from "./components/ApprovalLog";
import CustomField from "./components/customField/customField";
import Quotes from "./components/quotes/Quotes";
import { usePullQuotesPolling } from "./hooks/usePullQuotesPolling";
import "./ShipmentQuotes.scss";

const ShipmentQuotes = function (props) {
  const queryClient = useQueryClient();

  const id = useParams().id.toUpperCase();
  const { isQuickRequest } = props;

  // get query param "cancel_duplicate" from URL
  const urlParams = new URLSearchParams(window.location.search);
  const cancelDuplicate = urlParams.get("cancel_duplicate");

  const { state, actions } = useStateMachine({ updateBackgroundLoadingState });

  const methods = useForm();
  const { permissions } = usePermissions();
  const parcedPermissions = JSON.parse(permissions || "{}")?.permissions;
  const { edit_other_quote_requests } = parcedPermissions?.quoting || {};
  const { view_quote_pricing } = parcedPermissions?.booking || {};

  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [isRequest, setIsRequest] = useState(false);
  const pullQuotesRefetchInterval = usePullQuotesPolling();
  const { data: shipmentData } = useGetShipmentBasic(id, {
    enabled: !isQuickRequest && !!id,
    refetchOnMount: false,
  });
  const { data: providers } = useGetProviderOptions({
    shipmentId: id,
    options: {
      enabled: !isQuickRequest,
      refetchInterval: pullQuotesRefetchInterval,
    },
  });
  const { isInitialLoading: quoteDetailLoading, data: quoteDetailsData } = useGetQuoteRequestDetails({
    shipmentId: id,
    options: {
      enabled: !isQuickRequest,
    },
  });
  const { isInitialLoading: quickDetailLoading, data: quickDetailsData } = useGetQuickRequestDetails(id, {
    enabled: isQuickRequest,
    refetchOnMount: false,
  });
  const data = useMemo(
    () => (isQuickRequest ? quickDetailsData : quoteDetailsData) || {},
    [isQuickRequest, quickDetailsData, quoteDetailsData],
  );
  const loadingData = isQuickRequest ? quickDetailLoading : quoteDetailLoading;

  const offPlatformShipment = shipmentData?.shipment?.is_off_platform_shipment || false;
  // Check if the quote has been created by the user who is currently logged in
  const requestorEmail = isQuickRequest
    ? data?.organization?.quick_request_created_by_email
    : data?.organization?.requestor_email;
  const currentUserEmail = localStorage.getItem("user");
  const isRequestor = requestorEmail === currentUserEmail;

  const { data: customLongFieldStatus } = useGetCustomLongfield01({
    shipmentId: id,
    options: { enabled: isRequestor, select: (customLongfieldData) => customLongfieldData.status },
  });

  state.shipmentId = id;

  // Checks whether or not the shipment is still a quote/quick request or a shipment.
  useEffect(() => {
    if (!data) return;
    const shipmentStatus = ["Delayed", "In transit", "Incomplete", "Delivered", "Auto-closed", "Booked", "Canceled"];
    setIsRequest(!shipmentStatus.includes(data?.organization?.request_status));
  }, [data]);

  const fetchData = () => {
    actions.updateBackgroundLoadingState(true);
    queryClient.invalidateQueries({ queryKey: quoteRequestQueryKeys.quoteDetails(id) });
    queryClient.invalidateQueries({ queryKey: shipmentQueryKeys.shipmentQuotationActionHistoryDetails(id) });
  };

  const closeCancelRequestModal = () => {
    setShowCancelDialog(false);
  };

  return (
    <FormProvider {...methods}>
      <div>
        {!!data.quick_request_details?.length && (
          <CancelRequestBanner
            isRequest={isRequest}
            QuickRequestDetails={data.quick_request_details}
            setShowCancelDialog={setShowCancelDialog}
            canCancel={edit_other_quote_requests}
            loadingData={loadingData}
            shipmentId={id}
          />
        )}

        {cancelDuplicate && (
          <div className="mb-xl">
            <Alert variant="success" label="">
              <span className="font-semibold">
                <FormattedMessage
                  id="shipment-quotes.components.cancelRequestBanner.cancelShipmentAndDuplicateNotification"
                  defaultMessage="Shipment {shipmentId} successfully canceled."
                  values={{ shipmentId: cancelDuplicate }}
                />
              </span>
              <br />
              <FormattedMessage
                id="shipment-quotes.components.cancelRequestBanner.cancelShipmentAndDuplicateNotificationSubtitle"
                defaultMessage="Wait for the new quotations and select one to re-book the shipment."
              />
            </Alert>
          </div>
        )}
        <QuoteMultiDropBanner isQuickRequest={isQuickRequest} />

        {!isQuickRequest && (
          <Quotes
            id={id}
            requestStatus={data.organization?.request_status || ""}
            fetchData={fetchData}
            providers={providers}
            pullQuotesRefetchInterval={pullQuotesRefetchInterval}
          />
        )}

        {showCancelDialog && (
          <CancelRequestDialog
            createdByEmail={data?.organization.requestor_email}
            close={closeCancelRequestModal}
            isOpen={showCancelDialog}
            isQuickRequest={isQuickRequest}
            shipmentId={id}
            QuickRequestDetails={isQuickRequest ? data.organization : data.quick_request_details}
            loadingData={loadingData}
          />
        )}
        {!offPlatformShipment && (
          <div className="quotes-card-row">
            <div className="quotes-card-container v1-card-wrapper">
              <Comments isQuickRequest={isQuickRequest} />
            </div>

            {!isQuickRequest && (
              <div className="quotes-card-container v1-card-wrapper">
                <InvitedProviders />
              </div>
            )}

            {!isQuickRequest && view_quote_pricing && (
              <div md={6} className="quotes-card-container">
                <ApprovalLog shipmentId={id} />
              </div>
            )}

            <div md={6} className="quotes-card-container">
              <CustomField status={customLongFieldStatus} shipmentId={id} quotesData={data} loadingData={loadingData} />
            </div>
          </div>
        )}
      </div>
    </FormProvider>
  );
};

ShipmentQuotes.propTypes = {
  isQuickRequest: PropTypes.bool,
  setIsConsolidated: PropTypes.func,
};

ShipmentQuotes.defaultProps = {
  isQuickRequest: false,
  setIsConsolidated: () => {},
};

export default ShipmentQuotes;
